import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getScheduleSectionCode, getScheduleSectionLabel, isProjectLevelSection } from "../projectsScheduleHelpers";
import WorkTypeRelationPicker from "../EditWorkTypeDrawer/WorkTypeRelationPicker";
import "../EditWorkTypeDrawer/EditWorkTypeDrawer.css";

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M10 13a5 5 0 0 0 7.07 0l2.12-2.12a5 5 0 0 0-7.07-7.07L10.7 5.23"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M14 11a5 5 0 0 0-7.07 0L4.81 13.12a5 5 0 1 0 7.07 7.07L13.3 18.77"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const TemplateItemDrawer = ({
  open,
  item,
  section,
  sections = [],
  onClose,
  onSave,
  onDelete,
}) => {
  const [name, setName] = useState("");
  const [weight, setWeight] = useState(0);
  const [predecessorIds, setPredecessorIds] = useState([]);
  const [successorIds, setSuccessorIds] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !item) return;
    setName(item.name || "");
    setWeight(Number(item.unit_weight_percent) || 0);
    setPredecessorIds(Array.isArray(item.predecessor_ids) ? item.predecessor_ids : []);
    setSuccessorIds(Array.isArray(item.successor_ids) ? item.successor_ids : []);
    setError("");
  }, [open, item]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const isMilestone = Boolean(item?.is_milestone);
  const isProjectLevel = isProjectLevelSection(section);
  const sectionLabel = useMemo(
    () => getScheduleSectionLabel(section),
    [section],
  );

  const relationSections = useMemo(
    () =>
      sections.map((sec) => ({
        id: sec.id,
        name: sec.name,
        is_project_level: Boolean(sec.is_project_level),
        work_types: (sec.items || []).map((row) => ({
          id: row.id,
          name: row.name,
          is_milestone: Boolean(row.is_milestone),
        })),
      })),
    [sections],
  );

  const workTypeLookup = useMemo(() => {
    const map = new Map();
    for (const sec of relationSections) {
      const code = getScheduleSectionCode(sec);
      for (const wt of sec.work_types || []) {
        map.set(String(wt.id), {
          name: wt.name,
          sectionCode: code,
          isMilestone: Boolean(wt.is_milestone),
        });
      }
    }
    return map;
  }, [relationSections]);

  const getWorkTypeLabel = useCallback(
    (id) => {
      const found = workTypeLookup.get(String(id));
      if (!found) return `ID ${id}`;
      const label = found.isMilestone ? `◆ ${found.name}` : found.name;
      return found.sectionCode ? `${found.sectionCode} / ${label}` : label;
    },
    [workTypeLookup],
  );

  if (!open || !item) return null;

  const handleSave = () => {
    if (!name.trim()) {
      setError(isMilestone ? "Укажите название вехи" : "Укажите название вида работ");
      return;
    }
    onSave({
      ...item,
      name: name.trim(),
      unit_weight_percent: isMilestone || isProjectLevel ? 0 : Number(weight) || 0,
      predecessor_ids: predecessorIds,
      successor_ids: successorIds,
    });
    onClose();
  };

  return (
    <>
      <div className="edit-wt-drawer-backdrop" onClick={onClose} />
      <aside className="edit-wt-drawer" role="dialog" aria-modal="true">
        <div className="edit-wt-drawer__header">
          <div className="edit-wt-drawer__header-main">
            <div className="edit-wt-drawer__titles">
              <input
                className="edit-wt-drawer__name-input"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError("");
                }}
              />
              <div className="edit-wt-drawer__section">{sectionLabel}</div>
            </div>
          </div>
          <button
            type="button"
            className="edit-wt-drawer__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="edit-wt-drawer__body">
          {!isMilestone && !isProjectLevel && (
            <section className="edit-wt-drawer__block">
              <h3 className="edit-wt-drawer__block-title">Удельный вес</h3>
              <div className="edit-wt-drawer__weight-input">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
                <span className="edit-wt-drawer__weight-affix">%</span>
              </div>
            </section>
          )}

          <section className="edit-wt-drawer__block">
            <h3 className="edit-wt-drawer__block-title">
              Связь между видами работ, вехами и разделами
            </h3>
            <p className="edit-wt-drawer__help">
              Связи попадут в проект вместе с шаблоном. Сроки пользователь
              проставит уже на графике. После применения отдельные работы всё
              равно можно поправить.
            </p>
            <WorkTypeRelationPicker
              label="Предшественник"
              icon={LinkIcon}
              selectedIds={predecessorIds}
              onSelectedIdsChange={setPredecessorIds}
              excludedIds={successorIds}
              currentWorkTypeId={item.id}
              sections={relationSections}
              getWorkTypeLabel={getWorkTypeLabel}
            />
            <WorkTypeRelationPicker
              label="Последующий"
              icon={LinkIcon}
              selectedIds={successorIds}
              onSelectedIdsChange={setSuccessorIds}
              excludedIds={predecessorIds}
              currentWorkTypeId={item.id}
              sections={relationSections}
              getWorkTypeLabel={getWorkTypeLabel}
            />
          </section>

          {error && <div className="edit-wt-drawer__error">{error}</div>}
        </div>

        <div className="edit-wt-drawer__footer">
          <div className="edit-wt-drawer__footer-actions">
            <button
              type="button"
              className="edit-wt-drawer__save"
              onClick={handleSave}
            >
              Сохранить связи
            </button>
            <button
              type="button"
              className="edit-wt-drawer__cancel"
              onClick={onClose}
            >
              Отмена
            </button>
          </div>
          <button
            type="button"
            className="edit-wt-drawer__delete"
            onClick={() => onDelete?.(item)}
          >
            Удалить {isMilestone ? "веху" : "вид работ"}
          </button>
        </div>
      </aside>
    </>
  );
};

export default TemplateItemDrawer;
